/**
 * Action builders for meeting room lifecycle.
 */
import type { Room } from '../types';
import type { JoinMeetingOptions } from '../types/meeting';
import {
  destroyLiveKit,
  getAvailableCameras,
  initializeLiveKit,
  joinRoom as livekitJoin,
  leaveRoom as livekitLeave,
} from '../services/livekit';
import {
  createRoom as firestoreCreateRoom,
  generateRoomCode,
} from '../services/firebase/firestore';
import { getFirebaseAuth } from '../services/firebase/config';
import {
  buildParticipantState,
  fetchRoomOrThrow,
  mapRoomData,
  registerLiveKitCallbacks,
  startLiveKitStateReconciler,
  stopLiveKitStateReconciler,
} from './meetingStoreHandlers';
import type { MeetingStoreGet, MeetingStoreSet } from './meetingStoreTypes';

/**
 * Builds the create room action.
 */
export function createRoomAction(set: MeetingStoreSet) {
  return async (roomName: string, hostUserId: string): Promise<string> => {
    const roomCode = generateRoomCode();
    const roomData = await firestoreCreateRoom(roomCode, roomName, hostUserId);

    set({ room: mapRoomData(roomData) });
    return roomCode;
  };
}

/**
 * Builds the join room action.
 */
export function joinRoomAction(set: MeetingStoreSet, get: MeetingStoreGet) {
  return async (options: JoinMeetingOptions): Promise<void> => {
    set({ isConnecting: true, error: null });

    try {
      const room = await resolveRoomData(options);
      const participantId = await getParticipantId();
      const isHost = !!room && room.hostUserId === participantId;

      initializeLiveKit();
      registerLiveKitCallbacks(set, get);

      await livekitJoin({
        roomName: options.roomCode,
        displayName: options.displayName,
        participantId,
        audioEnabled: options.audioEnabled,
        videoEnabled: options.videoEnabled,
        avatarUrl: options.avatarUrl,
        isHost,
        isVR: options.isVR,
        microphoneDeviceId: options.microphoneDeviceId,
        cameraDeviceId: options.cameraDeviceId,
        speakerDeviceId: options.speakerDeviceId,
      });

      const cameras = await getAvailableCameras();
      set({
        room,
        isConnected: true,
        isConnecting: false,
        cameraDevices: cameras,
        currentCameraIndex: 0,
        ...buildParticipantState(),
      });

      startLiveKitStateReconciler(set);
    } catch (error) {
      stopLiveKitStateReconciler();
      const message = error instanceof Error ? error.message : 'Failed to join';
      set({ error: message, isConnecting: false });
      throw error;
    }
  };
}

/**
 * Resolves room metadata for joining.
 */
async function resolveRoomData(options: JoinMeetingOptions): Promise<Room | null> {
  if (options.roomName) {
    return {
      roomCode: options.roomCode,
      roomName: options.roomName,
      hostUserId: '',
      createdAt: new Date(),
      maxParticipants: 16,
    };
  }

  const roomData = await fetchRoomOrThrow(options.roomCode);
  return mapRoomData(roomData);
}

/**
 * Gets the current user's participant ID from Firebase auth.
 */
async function getParticipantId(): Promise<string> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (user) {
    return user.uid;
  }

  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Builds the leave room action.
 */
export function leaveRoomAction(set: MeetingStoreSet) {
  return async (): Promise<void> => {
    stopLiveKitStateReconciler();

    try {
      await livekitLeave();
      destroyLiveKit();

      set({
        room: null,
        participants: [],
        localParticipant: null,
        isConnected: false,
        isSharingScreen: false,
        screenShareOwnerId: null,
        screenShareTrack: null,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to leave';
      set({ error: message });
    }
  };
}
